import { nanoid } from 'nanoid'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'

// ============ 类型定义 ============

export interface Sticker {
  id: string
  name: string
  url: string
  order: number
  createdAt: number
}

export interface StickerImportData {
  name: string
  url: string
}

export interface StickerPack {
  id: string
  name: string
  description?: string
  coverUrl?: string
  stickers: Sticker[]
  createdAt: number
  updatedAt: number
}

export interface BulkAddResult {
  success: number
  failed: number
  skipped: number
  compressed?: number
  error?: string
}

// ============ 常量 ============

export const MAX_STICKERS_PER_PACK = 500
export const MAX_ZIP_SIZE = 100 * 1024 * 1024
export const STICKER_COMPRESS_THRESHOLD = 200 * 1024
export const STICKER_COMPRESSION_OPTIONS = {
  maxSizeMB: 0.15,
  maxWidthOrHeight: 512,
  useWebWorker: true,
  fileType: 'image/webp' as const,
  initialQuality: 0.8,
}

export function isSafeStickerUrl(url: string): boolean {
  if (!url || typeof url !== 'string')
    return false
  if (url.startsWith('data:image/')) {
    // SVG 可内嵌脚本，不允许作为 sticker
    if (url.startsWith('data:image/svg'))
      return false
    return true
  }
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  }
  catch {
    return false
  }
}

// ============ Store ============

export const useStickersStore = defineStore('stickers', () => {
  // 全局表情包组存储（IndexedDB）
  const stickerPacksStorage = useIndexedDBStorage<StickerPack[]>('stickers/packs', [], {
    debounceMs: 300,
  })
  const stickerPacks = stickerPacksStorage.data

  // 旧数据存储（仅用于迁移读取，新代码不写入）
  const legacyStorage = useIndexedDBStorage<Record<string, Sticker[]>>('chat/contact-stickers', {}, {
    debounceMs: 300,
  })
  const legacyContactStickersMap = legacyStorage.data

  // 存储加载状态
  const stickersStorageLoaded = ref(false)
  waitForStorage(stickerPacksStorage).then(() => {
    stickersStorageLoaded.value = true
    console.debug('✅ 表情包存储加载完成（IndexedDB）')
  })

  // 当前活动的联系人（用于 UI 上下文）
  const activeContactId = ref<string | null>(null)

  // ============ Pack CRUD ============

  function getPackById(packId: string): StickerPack | undefined {
    return stickerPacks.value.find(p => p.id === packId)
  }

  function createPack(name: string, description?: string): string {
    const id = nanoid()
    const now = Date.now()
    const newPack: StickerPack = {
      id,
      name,
      description,
      stickers: [],
      createdAt: now,
      updatedAt: now,
    }
    stickerPacks.value = [...stickerPacks.value, newPack]
    return id
  }

  function deletePack(packId: string): boolean {
    const index = stickerPacks.value.findIndex(p => p.id === packId)
    if (index === -1)
      return false
    stickerPacks.value = stickerPacks.value.filter(p => p.id !== packId)
    return true
  }

  function renamePack(packId: string, name: string): boolean {
    const index = stickerPacks.value.findIndex(p => p.id === packId)
    if (index === -1)
      return false
    const updated = [...stickerPacks.value]
    updated[index] = { ...updated[index], name, updatedAt: Date.now() }
    stickerPacks.value = updated
    return true
  }

  function updatePackCover(packId: string, coverUrl: string): boolean {
    const index = stickerPacks.value.findIndex(p => p.id === packId)
    if (index === -1)
      return false
    const updated = [...stickerPacks.value]
    updated[index] = { ...updated[index], coverUrl, updatedAt: Date.now() }
    stickerPacks.value = updated
    return true
  }

  function duplicatePack(packId: string): string | null {
    const pack = getPackById(packId)
    if (!pack)
      return null
    const id = nanoid()
    const now = Date.now()
    const newPack: StickerPack = {
      id,
      name: `${pack.name} (副本)`,
      description: pack.description,
      coverUrl: pack.coverUrl,
      stickers: pack.stickers.map(s => ({
        ...s,
        id: nanoid(),
      })),
      createdAt: now,
      updatedAt: now,
    }
    stickerPacks.value = [...stickerPacks.value, newPack]
    return id
  }

  // ============ Sticker CRUD (within Pack) ============

  function addStickerToPack(sticker: StickerImportData, packId: string): Sticker | null {
    const packIndex = stickerPacks.value.findIndex(p => p.id === packId)
    if (packIndex === -1)
      return null

    if (!isSafeStickerUrl(sticker.url))
      return null

    const pack = stickerPacks.value[packIndex]

    if (pack.stickers.length >= MAX_STICKERS_PER_PACK)
      return null

    // URL 去重
    const urlSet = new Set(pack.stickers.map(s => s.url))
    if (urlSet.has(sticker.url))
      return null

    const maxOrder = pack.stickers.length > 0
      ? Math.max(...pack.stickers.map(s => s.order))
      : 0

    const newSticker: Sticker = {
      id: nanoid(),
      name: sticker.name,
      url: sticker.url,
      order: maxOrder + 1,
      createdAt: Date.now(),
    }

    const updated = [...stickerPacks.value]
    updated[packIndex] = {
      ...pack,
      stickers: [...pack.stickers, newSticker],
      coverUrl: pack.coverUrl || sticker.url,
      updatedAt: Date.now(),
    }
    stickerPacks.value = updated

    return newSticker
  }

  function bulkAddStickersToPack(stickersData: StickerImportData[], packId: string): BulkAddResult {
    const packIndex = stickerPacks.value.findIndex(p => p.id === packId)
    if (packIndex === -1)
      return { success: 0, failed: 0, skipped: 0, error: '表情包组不存在' }

    const pack = stickerPacks.value[packIndex]
    const remaining = MAX_STICKERS_PER_PACK - pack.stickers.length

    if (remaining <= 0)
      return { success: 0, failed: 0, skipped: 0, error: `已达上限 ${MAX_STICKERS_PER_PACK} 个` }

    const toAdd = stickersData.slice(0, remaining)
    const urlSet = new Set(pack.stickers.map(s => s.url))
    let maxOrder = pack.stickers.length > 0
      ? Math.max(...pack.stickers.map(s => s.order))
      : 0

    let success = 0
    let skipped = 0
    let failed = 0
    const newStickers: Sticker[] = []

    for (const data of toAdd) {
      try {
        if (!isSafeStickerUrl(data.url)) {
          failed++
          continue
        }
        if (urlSet.has(data.url)) {
          skipped++
          continue
        }
        urlSet.add(data.url)
        maxOrder++
        newStickers.push({
          id: nanoid(),
          name: data.name,
          url: data.url,
          order: maxOrder,
          createdAt: Date.now(),
        })
        success++
      }
      catch {
        failed++
      }
    }

    if (newStickers.length > 0) {
      const updated = [...stickerPacks.value]
      updated[packIndex] = {
        ...pack,
        stickers: [...pack.stickers, ...newStickers],
        coverUrl: pack.coverUrl || newStickers[0]?.url,
        updatedAt: Date.now(),
      }
      stickerPacks.value = updated
    }

    const skippedByLimit = stickersData.length - toAdd.length
    return {
      success,
      failed,
      skipped: skipped + skippedByLimit,
      error: skippedByLimit > 0 ? `超出上限，${skippedByLimit} 个被跳过` : undefined,
    }
  }

  function deleteStickerFromPack(stickerId: string, packId: string): boolean {
    const packIndex = stickerPacks.value.findIndex(p => p.id === packId)
    if (packIndex === -1)
      return false

    const pack = stickerPacks.value[packIndex]
    const newStickers = pack.stickers.filter(s => s.id !== stickerId)
    if (newStickers.length === pack.stickers.length)
      return false

    const updated = [...stickerPacks.value]
    updated[packIndex] = { ...pack, stickers: newStickers, updatedAt: Date.now() }
    stickerPacks.value = updated
    return true
  }

  function updateStickerInPack(stickerId: string, updates: Partial<Omit<Sticker, 'id'>>, packId: string): boolean {
    const packIndex = stickerPacks.value.findIndex(p => p.id === packId)
    if (packIndex === -1)
      return false

    const pack = stickerPacks.value[packIndex]
    const stickerIndex = pack.stickers.findIndex(s => s.id === stickerId)
    if (stickerIndex === -1)
      return false

    const updatedStickers = [...pack.stickers]
    updatedStickers[stickerIndex] = { ...updatedStickers[stickerIndex], ...updates }

    const updated = [...stickerPacks.value]
    updated[packIndex] = { ...pack, stickers: updatedStickers, updatedAt: Date.now() }
    stickerPacks.value = updated
    return true
  }

  function moveStickerToTopInPack(stickerId: string, packId: string): boolean {
    const packIndex = stickerPacks.value.findIndex(p => p.id === packId)
    if (packIndex === -1)
      return false

    const pack = stickerPacks.value[packIndex]
    const stickerIndex = pack.stickers.findIndex(s => s.id === stickerId)
    if (stickerIndex === -1)
      return false

    const maxOrder = Math.max(...pack.stickers.map(s => s.order))
    const updatedStickers = [...pack.stickers]
    updatedStickers[stickerIndex] = { ...updatedStickers[stickerIndex], order: maxOrder + 1 }

    const updated = [...stickerPacks.value]
    updated[packIndex] = { ...pack, stickers: updatedStickers, updatedAt: Date.now() }
    stickerPacks.value = updated
    return true
  }

  // ============ 联系人聚合查询 ============

  function getStickersForPacks(packIds: string[]): Sticker[] {
    if (!packIds || packIds.length === 0)
      return []
    const urlSet = new Set<string>()
    const result: Sticker[] = []

    for (const packId of packIds) {
      const pack = getPackById(packId)
      if (!pack)
        continue
      for (const sticker of pack.stickers) {
        if (!urlSet.has(sticker.url)) {
          urlSet.add(sticker.url)
          result.push(sticker)
        }
      }
    }

    return result.sort((a, b) => b.createdAt - a.createdAt)
  }

  function getStickerNamesForPacks(packIds: string[]): string[] {
    return getStickersForPacks(packIds).map(s => s.name)
  }

  function findStickerByNameInPacks(name: string, packIds: string[]): Sticker | null {
    for (const packId of packIds) {
      const pack = getPackById(packId)
      if (!pack)
        continue
      const found = pack.stickers.find(s => s.name === name)
      if (found)
        return found
    }
    return null
  }

  function getStickerCountForPacks(packIds: string[]): number {
    return getStickersForPacks(packIds).length
  }

  // ============ 导入/导出 ============

  async function importStickersToPack(input: string, packId: string): Promise<BulkAddResult> {
    const pack = getPackById(packId)
    if (!pack)
      return { success: 0, failed: 0, skipped: 0, error: '表情包组不存在' }

    try {
      const parsed = JSON.parse(input.trim())
      if (Array.isArray(parsed)) {
        return bulkAddStickersToPack(parsed, packId)
      }
    }
    catch {
      // 纯文本格式解析
      const lines = input.trim().split('\n')
      const stickers: StickerImportData[] = []

      for (const line of lines) {
        const match = line.match(/(.+?):\s*(.+)/)
        if (match) {
          stickers.push({
            name: match[1].trim(),
            url: match[2].trim(),
          })
        }
      }

      if (stickers.length > 0) {
        return bulkAddStickersToPack(stickers, packId)
      }
    }

    return { success: 0, failed: 0, skipped: 0 }
  }

  function exportPackAsJson(packId: string): string {
    const pack = getPackById(packId)
    if (!pack)
      return '[]'
    const exportData = pack.stickers.map(s => ({ name: s.name, url: s.url }))
    return JSON.stringify(exportData, null, 2)
  }

  function exportPackAsText(packId: string): string {
    const pack = getPackById(packId)
    if (!pack)
      return ''
    return pack.stickers.map(s => `${s.name}: ${s.url}`).join('\n')
  }

  // ============ 兼容性方法（过渡期） ============

  function setActiveContact(contactId: string) {
    activeContactId.value = contactId
  }

  // 向后兼容：旧的 contactStickersMap（只读，供迁移期间使用）
  const contactStickersMap = computed(() => legacyContactStickersMap.value)

  // 向后兼容：currentStickerNames（需要外部传入 packIds 后才能用新逻辑）
  // 这个会在 useEnhancedPrompt 中被替换为 getStickerNamesForPacks
  const currentStickerNames = computed<string[]>(() => [])

  // 向后兼容：getStickerCount
  function getStickerCount(contactId?: string): number {
    // 过渡期：返回旧数据的数量（迁移后由 UI 层调用 getStickerCountForPacks）
    const targetId = contactId || activeContactId.value
    if (!targetId)
      return 0
    return (legacyContactStickersMap.value[targetId] || []).length
  }

  // 向后兼容：deleteContactStickers（清理旧数据用）
  function deleteContactStickers(contactId: string) {
    if (legacyContactStickersMap.value[contactId]) {
      const { [contactId]: _, ...rest } = legacyContactStickersMap.value
      legacyContactStickersMap.value = rest
    }
  }

  function getNeedRenameStickerIds(packId: string): string[] {
    const pack = getPackById(packId)
    if (!pack)
      return []
    return pack.stickers
      .filter((s) => {
        const n = s.name.trim()
        if (!n)
          return true
        if (/^[a-f0-9]{8,}$/i.test(n))
          return true
        if (/^(?:image|img|sticker|photo|pic|file|download|unnamed|untitled)[\s_-]?\d*$/i.test(n))
          return true
        if (/^[^a-z\u4E00-\u9FFF\u3040-\u30FF\uAC00-\uD7AF]{3,}$/i.test(n))
          return true
        return false
      })
      .map(s => s.id)
  }

  function updateStickerUrlGlobally(oldUrl: string, newUrl: string): void {
    let changed = false
    const updated = [...stickerPacks.value]
    for (let i = 0; i < updated.length; i++) {
      const pack = updated[i]
      const matchIndices = pack.stickers.reduce<number[]>((acc, s, idx) => {
        if (s.url === oldUrl)
          acc.push(idx)
        return acc
      }, [])
      if (matchIndices.length > 0) {
        const updatedStickers = [...pack.stickers]
        for (const idx of matchIndices) {
          updatedStickers[idx] = { ...updatedStickers[idx], url: newUrl }
        }
        updated[i] = { ...pack, stickers: updatedStickers, updatedAt: Date.now() }
        changed = true
      }
    }
    if (changed) {
      stickerPacks.value = updated
    }
  }

  return {
    // State
    stickerPacks,
    activeContactId,
    stickersStorageLoaded,

    // Pack CRUD
    getPackById,
    createPack,
    deletePack,
    renamePack,
    updatePackCover,
    duplicatePack,

    // Sticker CRUD (within Pack)
    addStickerToPack,
    bulkAddStickersToPack,
    deleteStickerFromPack,
    updateStickerInPack,
    moveStickerToTopInPack,

    // 联系人聚合查询
    getStickersForPacks,
    getStickerNamesForPacks,
    findStickerByNameInPacks,
    getStickerCountForPacks,

    // 自动命名辅助
    getNeedRenameStickerIds,

    // 懒压缩：按 URL 查找并更新表情包图片
    updateStickerUrlGlobally,

    // 导入/导出
    importStickersToPack,
    exportPackAsJson,
    exportPackAsText,

    // 兼容性（过渡期）
    contactStickersMap,
    currentStickerNames,
    setActiveContact,
    getStickerCount,
    deleteContactStickers,
  }
})
